// MetricsRow.js - Horizontal row of metric cards for Asset Details Redux (PRD-43)
// Wires asset + enrichment + drift data to the MetricsCard components

import React from 'react';
import MetricsCard from './MetricsCard';
import './MetricsRow.css';

/**
 * Maps a numeric scan risk score (0-100) to a severity label + band.
 * R3DTEAM convention: lower score = worse (inverted).
 */
function scanScoreSeverity(score) {
    if (score == null || isNaN(score)) return { label: 'UNKNOWN', band: 'neutral' };
    if (score < 30) return { label: 'CRITICAL', band: 'critical' };
    if (score < 50) return { label: 'HIGH', band: 'high' };
    if (score < 70) return { label: 'MEDIUM', band: 'medium' };
    if (score < 85) return { label: 'LOW', band: 'low' };
    return { label: 'HEALTHY', band: 'low' };
}

/**
 * Formats the dominant severity of the vuln breakdown for the band color.
 */
function vulnDominantBand({ critical = 0, high = 0, medium = 0, low = 0 }) {
    if (critical > 0) return 'critical';
    if (high > 0) return 'high';
    if (medium > 0) return 'medium';
    if (low > 0) return 'low';
    return 'neutral';
}

/**
 * @param {object} props
 * @param {object} props.asset - Asset entity
 * @param {object} [props.enrichment] - Enrichment object (aiAnalysis)
 * @param {object} [props.driftSummary] - Optional { openCount, latestEvent }
 * @param {object} [props.handlers] - Click handlers: { onVulnClick, onPortsClick, onSubdomainsClick, onDriftClick, onScoreClick }
 */
function MetricsRow({ asset = {}, enrichment, driftSummary, handlers = {} }) {
    // Scan score (from asset entity per PRD-40 - current scan risk)
    const scanScore = asset.currentRiskScore ?? null;
    const scanSev = scanScoreSeverity(scanScore);

    // AI score (baseline from Claude enrichment)
    const aiScore = asset.baselineRiskScore ?? enrichment?.baselineRiskScore ?? null;
    const aiLevel = asset.currentRiskLevel || asset.baselineRiskLevel || null;

    // Vuln counts (from asset entity per PRD-40)
    const vulnCounts = {
        critical: asset.criticalCount ?? 0,
        high: asset.highCount ?? 0,
        medium: asset.mediumCount ?? 0,
        low: asset.lowCount ?? 0
    };
    const totalVulns = vulnCounts.critical + vulnCounts.high + vulnCounts.medium + vulnCounts.low;
    const vulnBand = vulnDominantBand(vulnCounts);
    const vulnHint = `${vulnCounts.critical}C ${vulnCounts.high}H ${vulnCounts.medium}M ${vulnCounts.low}L`;

    // Open ports
    const openPorts = asset.openPortCount ?? null;

    // Subdomains (API exposes subdomainCount; live count is optional)
    const subdomainsTotal = asset.subdomainCount ?? null;
    const subdomainsLive = asset.liveSubdomainCount ?? null;

    // Drift
    const driftOpen = driftSummary?.openCount ?? asset.openDriftEvents ?? 0;
    const driftBand = driftOpen > 0 ? 'high' : 'low';

    return (
        <div className="metrics-row" role="group" aria-label="Asset metrics">
            <MetricsCard
                label="Scan Score"
                value={scanScore != null ? scanScore : '—'}
                sublabel={scanSev.label}
                severity={scanSev.band}
                secondary={
                    aiScore != null
                        ? `AI: ${aiScore}${aiLevel ? ' ' + String(aiLevel).toUpperCase() : ''}`
                        : (aiLevel ? `AI: ${String(aiLevel).toUpperCase()}` : null)
                }
                onClick={handlers.onScoreClick}
                ariaLabel={`Scan score ${scanScore}, ${scanSev.label}`}
            />

            <MetricsCard
                label="Vulnerabilities"
                value={totalVulns}
                hint={vulnHint}
                severity={vulnBand}
                onClick={handlers.onVulnClick}
                ariaLabel={`${totalVulns} total vulnerabilities`}
            />

            <MetricsCard
                label="Open Ports"
                value={openPorts != null ? openPorts : '—'}
                severity="neutral"
                onClick={handlers.onPortsClick}
                ariaLabel={openPorts != null ? `${openPorts} open ports` : 'Open ports unknown'}
            />

            <MetricsCard
                label="Subdomains"
                value={subdomainsTotal != null ? subdomainsTotal : '—'}
                hint={subdomainsLive != null && subdomainsTotal != null ? `${subdomainsLive} live` : undefined}
                severity="neutral"
                onClick={handlers.onSubdomainsClick}
                ariaLabel={subdomainsTotal != null ? `${subdomainsTotal} subdomains` : 'Subdomains unknown'}
            />

            <MetricsCard
                label="Drift"
                value={driftOpen > 0 ? 'ALERT' : 'Stable'}
                hint={driftOpen > 0 ? `${driftOpen} open event${driftOpen === 1 ? '' : 's'}` : 'No open events'}
                severity={driftBand}
                onClick={handlers.onDriftClick}
                ariaLabel={driftOpen > 0 ? `${driftOpen} open drift events` : 'No drift events'}
            />
        </div>
    );
}

export default MetricsRow;
